// ============================================================================
// DRAWING VIEWER - Full-screen viewer with markup/annotation tools
// Date: January 2026
// ============================================================================

'use client';

/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps -- reset pan/zoom when opening a different drawing */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, ZoomIn, ZoomOut, RotateCw, Maximize, Minimize,
  Download, Share2, Info, ChevronLeft, ChevronRight,
  Edit2, MessageSquare, Layers, History, Printer,
  Move, MousePointer, PenTool, Square, Circle,
  Type, ArrowRight, Cloud, Ruler, Undo, Redo,
  Trash2, Save, Eye, EyeOff, Palette, Menu,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Drawing, DrawingMarkup, MarkupType, MarkupData, DrawingComment } from './types';
import { STATUS_CONFIG, FILE_TYPE_CONFIG } from './types';
import { resolveDrawingFileUrl } from './drawingFileUrl';
import CommentsSection from '@/components/work-items/CommentsSection';

// ============================================================================
// TYPES
// ============================================================================

interface DrawingViewerProps {
  drawing: Drawing | null;
  isOpen: boolean;
  onClose: () => void;
  onApprove?: (id: string) => void;
  onPublish?: (id: string) => void;
  drawings?: Drawing[]; // For navigation
  onNavigate?: (drawing: Drawing) => void;
  mode?: 'view' | 'markup' | 'comment';
  /** Fallback work item for `/work-items/:id/comments` when `drawing.work_item_id` is not set (e.g. opened from job context). */
  commentsWorkItemId?: string | null;
}

interface Point {
  x: number;
  y: number;
}

interface ActiveMarkup {
  type: MarkupType;
  points: Point[];
  text?: string;
  color: string;
  strokeWidth: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const MARKUP_TOOLS: { type: MarkupType; icon: LucideIcon; label: string }[] = [
  { type: 'arrow', icon: ArrowRight, label: 'Arrow' },
  { type: 'rectangle', icon: Square, label: 'Rectangle' },
  { type: 'circle', icon: Circle, label: 'Circle' },
  { type: 'line', icon: PenTool, label: 'Line' },
  { type: 'freehand', icon: PenTool, label: 'Freehand' },
  { type: 'cloud', icon: Cloud, label: 'Revision Cloud' },
  { type: 'text', icon: Type, label: 'Text' },
  { type: 'callout', icon: MessageSquare, label: 'Callout' },
  { type: 'dimension', icon: Ruler, label: 'Dimension' },
];

const COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', 
  '#8b5cf6', '#ec4899', '#000000', '#ffffff'
];

const STROKE_WIDTHS = [1, 2, 3, 4, 6, 8];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const canPreviewAsImage = (fileType: string): boolean => {
  const type = fileType?.toLowerCase() || '';
  return ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(type);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// ============================================================================
// MARKUP CANVAS COMPONENT
// ============================================================================

interface MarkupCanvasProps {
  imageUrl: string;
  markups: DrawingMarkup[];
  activeMarkup: ActiveMarkup | null;
  activeTool: MarkupType | 'select' | 'pan' | null;
  viewMode: 'view' | 'markup' | 'comment';
  onMarkupComplete: (markup: Omit<DrawingMarkup, 'id' | 'drawing_id' | 'user_id' | 'created_at' | 'updated_at'>) => void;
  zoom: number;
  onZoomChange: (nextZoom: number) => void;
  rotation: number;
  pan: Point;
  onPanChange: (pan: Point) => void;
  showMarkups: boolean;
}

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 5;

function clampZoom(z: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z));
}

function touchDistance(a: { clientX: number; clientY: number }, b: { clientX: number; clientY: number }): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy) || 1;
}

function MarkupCanvas({
  imageUrl,
  markups,
  activeMarkup,
  activeTool,
  viewMode,
  onMarkupComplete,
  zoom,
  onZoomChange,
  rotation,
  pan,
  onPanChange,
  showMarkups,
}: MarkupCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const imageRef = useRef<HTMLImageElement | null>(null);
  const pinchRef = useRef<{ initialDistance: number; initialZoom: number } | null>(null);
  const lastTouchPan = useRef<{ x: number; y: number } | null>(null);
  const currentPointsRef = useRef<Point[]>([]);

  useEffect(() => {
    currentPointsRef.current = currentPoints;
  }, [currentPoints]);

  function drawMarkup(
    ctx: CanvasRenderingContext2D,
    type: MarkupType,
    points: Point[],
    color: string,
    strokeWidth: number,
    text?: string
  ) {
    if (points.length === 0) return;

    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (type) {
      case 'arrow':
        if (points.length >= 2) {
          const start = points[0];
          const end = points[points.length - 1];

          // Line
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();

          // Arrowhead
          const angle = Math.atan2(end.y - start.y, end.x - start.x);
          const headLength = 15;
          ctx.beginPath();
          ctx.moveTo(end.x, end.y);
          ctx.lineTo(end.x - headLength * Math.cos(angle - Math.PI / 6), end.y - headLength * Math.sin(angle - Math.PI / 6));
          ctx.moveTo(end.x, end.y);
          ctx.lineTo(end.x - headLength * Math.cos(angle + Math.PI / 6), end.y - headLength * Math.sin(angle + Math.PI / 6));
          ctx.stroke();
        }
        break;

      case 'rectangle':
        if (points.length >= 2) {
          const start = points[0];
          const end = points[points.length - 1];
          ctx.strokeRect(start.x, start.y, end.x - start.x, end.y - start.y);
        }
        break;

      case 'circle':
        if (points.length >= 2) {
          const start = points[0];
          const end = points[points.length - 1];
          const radius = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
          ctx.beginPath();
          ctx.arc(start.x, start.y, radius, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;

      case 'line':
      case 'freehand':
        if (points.length >= 2) {
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          points.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
          ctx.stroke();
        }
        break;

      case 'cloud':
        if (points.length >= 2) {
          const start = points[0];
          const end = points[points.length - 1];
          const width = Math.abs(end.x - start.x);
          const height = Math.abs(end.y - start.y);
          const minX = Math.min(start.x, end.x);
          const minY = Math.min(start.y, end.y);

          // Draw cloud shape with bumps
          ctx.beginPath();
          const bumps = Math.max(4, Math.floor(width / 20));
          const bumpRadius = width / bumps / 2;

          for (let i = 0; i < bumps; i++) {
            const x = minX + (i + 0.5) * (width / bumps);
            ctx.arc(x, minY, bumpRadius, Math.PI, 0);
          }
          for (let i = 0; i < Math.max(2, Math.floor(height / 20)); i++) {
            const y = minY + (i + 0.5) * (height / Math.max(2, Math.floor(height / 20)));
            ctx.arc(minX + width, y, bumpRadius, -Math.PI / 2, Math.PI / 2);
          }
          for (let i = bumps - 1; i >= 0; i--) {
            const x = minX + (i + 0.5) * (width / bumps);
            ctx.arc(x, minY + height, bumpRadius, 0, Math.PI);
          }
          for (let i = Math.max(2, Math.floor(height / 20)) - 1; i >= 0; i--) {
            const y = minY + (i + 0.5) * (height / Math.max(2, Math.floor(height / 20)));
            ctx.arc(minX, y, bumpRadius, Math.PI / 2, -Math.PI / 2);
          }
          ctx.closePath();
          ctx.stroke();
        }
        break;

      case 'text':
        if (points.length >= 1 && text) {
          ctx.font = `${strokeWidth * 6}px sans-serif`;
          ctx.fillText(text, points[0].x, points[0].y);
        }
        break;

      case 'callout':
        if (points.length >= 2 && text) {
          const start = points[0];
          const end = points[points.length - 1];

          // Text box
          ctx.font = `${strokeWidth * 5}px sans-serif`;
          const textWidth = ctx.measureText(text).width;
          const padding = 8;

          ctx.fillStyle = '#ffffff';
          ctx.fillRect(end.x, end.y - 20, textWidth + padding * 2, 28);
          ctx.strokeRect(end.x, end.y - 20, textWidth + padding * 2, 28);

          // Leader line
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();

          // Text
          ctx.fillStyle = color;
          ctx.fillText(text, end.x + padding, end.y);
        }
        break;

      case 'dimension':
        if (points.length >= 2) {
          const start = points[0];
          const end = points[points.length - 1];
          const distance = Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));
          const midX = (start.x + end.x) / 2;
          const midY = (start.y + end.y) / 2;

          // Line
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();

          // End ticks
          const angle = Math.atan2(end.y - start.y, end.x - start.x) + Math.PI / 2;
          const tickSize = 10;

          ctx.beginPath();
          ctx.moveTo(start.x + tickSize * Math.cos(angle), start.y + tickSize * Math.sin(angle));
          ctx.lineTo(start.x - tickSize * Math.cos(angle), start.y - tickSize * Math.sin(angle));
          ctx.moveTo(end.x + tickSize * Math.cos(angle), end.y + tickSize * Math.sin(angle));
          ctx.lineTo(end.x - tickSize * Math.cos(angle), end.y - tickSize * Math.sin(angle));
          ctx.stroke();

          // Distance text
          ctx.font = '12px sans-serif';
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(midX - 25, midY - 10, 50, 16);
          ctx.fillStyle = color;
          ctx.textAlign = 'center';
          ctx.fillText(`${distance.toFixed(0)}px`, midX, midY + 3);
          ctx.textAlign = 'start';
        }
        break;
    }
  }

  // Load image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });
      setImageLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const applySize = () => {
      const w = Math.max(1, container.clientWidth);
      const h = Math.max(1, container.clientHeight);
      setCanvasSize({ width: w, height: h });
      canvas.width = w;
      canvas.height = h;
    };

    applySize();
    const ro = new ResizeObserver(applySize);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const next = clampZoom(zoomRef.current * Math.exp(-e.deltaY * 0.0015));
      onZoomChange(next);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onZoomChange]);

  // Redraw canvas
  useEffect(() => {
    if (!canvasRef.current || !imageLoaded || !imageRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply transforms
    ctx.save();
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);
    ctx.translate(canvas.width / 2 / zoom, canvas.height / 2 / zoom);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-imageSize.width / 2, -imageSize.height / 2);

    // Draw image
    ctx.drawImage(imageRef.current, 0, 0);

    // Draw markups
    if (showMarkups) {
      markups.forEach(markup => {
        drawMarkup(ctx, markup.markup_type, markup.markup_data.points || [], markup.color, markup.markup_data.strokeWidth || 2, markup.markup_data.text);
      });
    }

    // Draw current markup being created
    if (currentPoints.length > 0 && activeMarkup) {
      drawMarkup(ctx, activeMarkup.type, currentPoints, activeMarkup.color, activeMarkup.strokeWidth, activeMarkup.text);
    }

    ctx.restore();
  }, [imageLoaded, markups, currentPoints, activeMarkup, zoom, rotation, pan, showMarkups, imageSize, canvasSize.width, canvasSize.height]);

  const allowMarkupDraw =
    viewMode === 'markup'
    && activeTool != null
    && activeTool !== 'select'
    && activeTool !== 'pan';

  const allowPanDrag =
    viewMode === 'view'
    || viewMode === 'comment'
    || activeTool === 'pan'
    || activeTool === 'select'
    || activeTool === null
    || !allowMarkupDraw;

  const getCanvasPointFromClient = (clientX: number, clientY: number): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const x = (clientX - rect.left - pan.x) / zoom - canvas.width / 2 / zoom + imageSize.width / 2;
    const y = (clientY - rect.top - pan.y) / zoom - canvas.height / 2 / zoom + imageSize.height / 2;

    return { x, y };
  };

  const getCanvasPoint = (e: React.MouseEvent): Point => getCanvasPointFromClient(e.clientX, e.clientY);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (allowMarkupDraw) {
      setIsDrawing(true);
      setCurrentPoints([getCanvasPoint(e)]);
      return;
    }
    if (allowPanDrag) {
      setIsPanning(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isPanning) {
      onPanChange({ x: pan.x + e.movementX, y: pan.y + e.movementY });
      return;
    }

    if (!isDrawing || !allowMarkupDraw) return;

    const point = getCanvasPoint(e);
    if (activeTool === 'freehand') {
      setCurrentPoints(prev => [...prev, point]);
    } else {
      setCurrentPoints(prev => (prev.length > 0 ? [prev[0], point] : [point]));
    }
  };

  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }
    if (!isDrawing) return;
    setIsDrawing(false);

    if (allowMarkupDraw && activeMarkup && currentPointsRef.current.length >= 1) {
      onMarkupComplete({
        markup_type: activeMarkup.type,
        markup_data: {
          points: currentPointsRef.current,
          text: activeMarkup.text,
          strokeWidth: activeMarkup.strokeWidth,
        },
        layer_name: 'default',
        color: activeMarkup.color,
        is_visible: true,
      });
    }

    setCurrentPoints([]);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchRef.current = {
        initialDistance: touchDistance(e.touches[0], e.touches[1]),
        initialZoom: zoomRef.current,
      };
      setIsPanning(false);
      setIsDrawing(false);
      lastTouchPan.current = null;
      return;
    }

    if (e.touches.length === 1) {
      pinchRef.current = null;
      const t = e.touches[0];

      if (allowMarkupDraw) {
        setIsDrawing(true);
        setCurrentPoints([getCanvasPointFromClient(t.clientX, t.clientY)]);
        return;
      }

      if (allowPanDrag) {
        setIsPanning(true);
        lastTouchPan.current = { x: t.clientX, y: t.clientY };
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const d = touchDistance(e.touches[0], e.touches[1]);
      const ratio = d / pinchRef.current.initialDistance;
      onZoomChange(clampZoom(pinchRef.current.initialZoom * ratio));
      return;
    }

    if (isPanning && e.touches.length === 1 && lastTouchPan.current) {
      e.preventDefault();
      const t = e.touches[0];
      const prev = lastTouchPan.current;
      onPanChange({ x: pan.x + (t.clientX - prev.x), y: pan.y + (t.clientY - prev.y) });
      lastTouchPan.current = { x: t.clientX, y: t.clientY };
      return;
    }

    if (!isDrawing || !allowMarkupDraw || e.touches.length !== 1) return;
    const t = e.touches[0];
    const point = getCanvasPointFromClient(t.clientX, t.clientY);
    if (activeTool === 'freehand') {
      setCurrentPoints(prev => [...prev, point]);
    } else {
      setCurrentPoints(prev => (prev.length > 0 ? [prev[0], point] : [point]));
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      pinchRef.current = null;
    }

    if (e.touches.length === 0) {
      lastTouchPan.current = null;
      if (isPanning) setIsPanning(false);
      if (isDrawing && allowMarkupDraw) {
        setIsDrawing(false);
        const pts = currentPointsRef.current;
        if (allowMarkupDraw && activeMarkup && pts.length >= 1) {
          onMarkupComplete({
            markup_type: activeMarkup.type,
            markup_data: {
              points: pts,
              text: activeMarkup.text,
              strokeWidth: activeMarkup.strokeWidth,
            },
            layer_name: 'default',
            color: activeMarkup.color,
            is_visible: true,
          });
        }
        setCurrentPoints([]);
      }
    }
  };

  const cursorClass = isPanning
    ? 'cursor-grabbing'
    : allowMarkupDraw
      ? 'cursor-crosshair'
      : allowPanDrag
        ? 'cursor-grab'
        : 'cursor-default';

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full touch-none overflow-hidden bg-gray-900"
      style={{ touchAction: 'none' }}
    >
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className={`h-full w-full ${cursorClass}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          if (isPanning) setIsPanning(false);
          handleMouseUp();
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />
    </div>
  );
}

// ============================================================================
// MAIN VIEWER COMPONENT
// ============================================================================

export default function DrawingViewer({
  drawing,
  isOpen,
  onClose,
  onApprove,
  onPublish,
  drawings = [],
  onNavigate,
  mode = 'view',
  commentsWorkItemId: commentsWorkItemIdProp = null,
}: DrawingViewerProps) {
  // View state
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Markup state
  const [viewMode, setViewMode] = useState<'view' | 'markup' | 'comment'>(mode);
  const [activeTool, setActiveTool] = useState<MarkupType | 'select' | 'pan' | null>(null);
  const [activeColor, setActiveColor] = useState('#ef4444');
  const [activeStrokeWidth, setActiveStrokeWidth] = useState(2);
  const [markups, setMarkups] = useState<DrawingMarkup[]>([]);
  const [showMarkups, setShowMarkups] = useState(true);
  const [markupHistory, setMarkupHistory] = useState<DrawingMarkup[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [textInput, setTextInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);

  // Reset state when drawing changes
  useEffect(() => {
    if (drawing) {
      setZoom(1);
      setRotation(0);
      setPan({ x: 0, y: 0 });
      setShowInfo(false);
      setMarkups([]);
      setMarkupHistory([]);
      setHistoryIndex(-1);
    }
  }, [drawing?.id]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setMarkups(markupHistory[historyIndex - 1]);
    } else if (historyIndex === 0) {
      setHistoryIndex(-1);
      setMarkups([]);
    }
  }, [historyIndex, markupHistory]);

  const handleRedo = useCallback(() => {
    if (historyIndex < markupHistory.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setMarkups(markupHistory[historyIndex + 1]);
    }
  }, [historyIndex, markupHistory]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        if (isFullscreen) {
          document.exitFullscreen();
          setIsFullscreen(false);
        } else if (activeTool) {
          setActiveTool(null);
        } else {
          onClose();
        }
      } else if (e.key === '+' || e.key === '=') {
        setZoom(z => Math.min(z + 0.25, 5));
      } else if (e.key === '-') {
        setZoom(z => Math.max(z - 0.25, 0.25));
      } else if (e.key === 'r' || e.key === 'R') {
        setRotation(r => (r + 90) % 360);
      } else if (e.key === 'i' || e.key === 'I') {
        setShowInfo(s => !s);
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, isFullscreen, activeTool, onClose, handleUndo, handleRedo]);

  const handleMarkupComplete = (markup: Omit<DrawingMarkup, 'id' | 'drawing_id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    const newMarkup: DrawingMarkup = {
      ...markup,
      id: `temp-${Date.now()}`,
      drawing_id: drawing?.id || '',
      user_id: 0,
      user_name: 'Current User',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const newMarkups = [...markups, newMarkup];
    setMarkups(newMarkups);
    
    // Update history
    const newHistory = markupHistory.slice(0, historyIndex + 1);
    newHistory.push(newMarkups);
    setMarkupHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleSaveMarkups = async () => {
    // TODO: Save markups to API
    console.log('Saving markups:', markups);
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleDownload = () => {
    if (drawing) {
      const a = document.createElement('a');
      a.href = resolveDrawingFileUrl(drawing.file_url);
      a.download = drawing.original_filename;
      a.target = '_blank';
      a.click();
    }
  };

  // Navigation
  const currentIndex = drawings.findIndex(d => d.id === drawing?.id);
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < drawings.length - 1;

  if (!isOpen || !drawing) return null;

  const fileType = drawing.file_type?.toLowerCase() || '';
  const fileConfig = FILE_TYPE_CONFIG[fileType] || { icon: '📁', label: fileType.toUpperCase(), canPreview: false };
  const statusConfig = STATUS_CONFIG[drawing.status] || STATUS_CONFIG.draft;
  const canPreview = canPreviewAsImage(fileType) || fileType === 'pdf';
  const fullUrl = resolveDrawingFileUrl(drawing.file_url);
  const effectiveCommentsWorkItemId =
    commentsWorkItemIdProp?.trim() ||
    (typeof drawing.work_item_id === 'string' ? drawing.work_item_id.trim() : '') ||
    '';

  return (
    <div ref={containerRef} className="fixed inset-0 bg-black z-[200] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gradient-to-b from-black/80 to-transparent absolute top-0 left-0 right-0 z-10">
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-white truncate">{drawing.drawing_name}</h2>
          <div className="flex items-center gap-3 text-sm">
            <span className={`px-2 py-0.5 rounded text-xs ${statusConfig.bgColor} ${statusConfig.color}`}>
              {statusConfig.icon} {statusConfig.label}
            </span>
            {drawing.drawing_number && (
              <span className="text-gray-400">{drawing.drawing_number} Rev {drawing.revision}</span>
            )}
            <span className="text-gray-400">{fileConfig.icon} {fileConfig.label}</span>
            <span className="text-gray-400">{formatFileSize(drawing.file_size)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Mode toggle */}
          <div className="flex rounded-lg bg-white/10 p-1">
            <button
              onClick={() => { setViewMode('view'); setActiveTool(null); }}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'view' ? 'bg-white/20 text-white' : 'text-gray-400'}`}
            >
              View
            </button>
            <button
              onClick={() => setViewMode('markup')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'markup' ? 'bg-white/20 text-white' : 'text-gray-400'}`}
            >
              Markup
            </button>
            <button
              onClick={() => setViewMode('comment')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'comment' ? 'bg-white/20 text-white' : 'text-gray-400'}`}
            >
              Comments
            </button>
          </div>

          <button onClick={() => setShowInfo(!showInfo)} className={`p-2 rounded-lg ${showInfo ? 'bg-blue-500' : 'bg-white/10'} text-white`}>
            <Info className="w-5 h-5" />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Markup toolbar */}
      {viewMode === 'markup' && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 p-2 rounded-lg bg-gray-900/90 backdrop-blur">
          {/* Selection tool */}
          <button
            onClick={() => setActiveTool(activeTool === 'select' ? null : 'select')}
            className={`p-2 rounded ${activeTool === 'select' ? 'bg-blue-500 text-white' : 'text-gray-300 hover:bg-white/10'}`}
            title="Select"
          >
            <MousePointer className="w-5 h-5" />
          </button>
          
          {/* Pan tool */}
          <button
            onClick={() => setActiveTool(activeTool === 'pan' ? null : 'pan')}
            className={`p-2 rounded ${activeTool === 'pan' ? 'bg-blue-500 text-white' : 'text-gray-300 hover:bg-white/10'}`}
            title="Pan"
          >
            <Move className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-600 mx-1" />

          {/* Markup tools */}
          {MARKUP_TOOLS.map(tool => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.type}
                onClick={() => setActiveTool(activeTool === tool.type ? null : tool.type)}
                className={`p-2 rounded ${activeTool === tool.type ? 'bg-blue-500 text-white' : 'text-gray-300 hover:bg-white/10'}`}
                title={tool.label}
              >
                <Icon className="w-5 h-5" />
              </button>
            );
          })}

          <div className="w-px h-6 bg-gray-600 mx-1" />

          {/* Color picker */}
          <div className="flex items-center gap-1">
            {COLORS.slice(0, 5).map(color => (
              <button
                key={color}
                onClick={() => setActiveColor(color)}
                className={`w-6 h-6 rounded-full border-2 ${activeColor === color ? 'border-white' : 'border-transparent'}`}
                style={{ backgroundColor: color }}
              />
            ))}
            <button className="p-1 rounded hover:bg-white/10">
              <Palette className="w-4 h-4 text-gray-300" />
            </button>
          </div>

          <div className="w-px h-6 bg-gray-600 mx-1" />

          {/* Stroke width */}
          <select
            value={activeStrokeWidth}
            onChange={(e) => setActiveStrokeWidth(Number(e.target.value))}
            className="bg-white/10 text-white text-sm rounded px-2 py-1"
          >
            {STROKE_WIDTHS.map(w => (
              <option key={w} value={w}>{w}px</option>
            ))}
          </select>

          <div className="w-px h-6 bg-gray-600 mx-1" />

          {/* Undo/Redo */}
          <button
            onClick={handleUndo}
            disabled={historyIndex < 0}
            className="p-2 rounded text-gray-300 hover:bg-white/10 disabled:opacity-30"
            title="Undo"
          >
            <Undo className="w-5 h-5" />
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= markupHistory.length - 1}
            className="p-2 rounded text-gray-300 hover:bg-white/10 disabled:opacity-30"
            title="Redo"
          >
            <Redo className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-600 mx-1" />

          {/* Visibility toggle */}
          <button
            onClick={() => setShowMarkups(!showMarkups)}
            className={`p-2 rounded ${showMarkups ? 'text-gray-300' : 'text-gray-500'} hover:bg-white/10`}
            title={showMarkups ? 'Hide Markups' : 'Show Markups'}
          >
            {showMarkups ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </button>

          {/* Save */}
          <button
            onClick={handleSaveMarkups}
            disabled={markups.length === 0}
            className="px-3 py-1.5 rounded bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-30 flex items-center gap-1"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 min-h-0 overflow-hidden pt-14 flex-col lg:flex-row">
        {/* Viewer */}
        <div className="relative min-h-0 min-w-0 flex-1">
          {canPreviewAsImage(fileType) ? (
            <MarkupCanvas
              imageUrl={fullUrl}
              markups={markups}
              activeMarkup={activeTool && activeTool !== 'select' && activeTool !== 'pan' ? {
                type: activeTool as MarkupType,
                points: [],
                color: activeColor,
                strokeWidth: activeStrokeWidth
              } : null}
              activeTool={activeTool}
              viewMode={viewMode}
              onMarkupComplete={handleMarkupComplete}
              zoom={zoom}
              onZoomChange={(z) => setZoom(Math.max(0.25, Math.min(5, z)))}
              rotation={rotation}
              pan={pan}
              onPanChange={setPan}
              showMarkups={showMarkups}
            />
          ) : fileType === 'pdf' ? (
            <iframe src={fullUrl} className="w-full h-full bg-white" title={drawing.drawing_name} />
          ) : (
            <div className="flex-1 flex items-center justify-center text-white">
              <div className="text-center">
                <span className="text-8xl block mb-4">{fileConfig.icon}</span>
                <h3 className="text-xl font-semibold mb-2">{fileConfig.label} File</h3>
                <p className="text-gray-400 mb-4">Preview not available</p>
                <button onClick={handleDownload} className="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto">
                  <Download className="w-5 h-5" />
                  Download ({formatFileSize(drawing.file_size)})
                </button>
              </div>
            </div>
          )}

          {/* Navigation arrows */}
          {canGoBack && onNavigate && (
            <button
              onClick={() => onNavigate(drawings[currentIndex - 1])}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {canGoForward && onNavigate && (
            <button
              onClick={() => onNavigate(drawings[currentIndex + 1])}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 hover:bg-black/70 text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {viewMode === 'comment' && (
          <div className="flex max-h-[45vh] w-full shrink-0 flex-col border-t border-gray-800 bg-gray-950 lg:max-h-none lg:w-[22rem] lg:border-l lg:border-t-0">
            <div className="border-b border-gray-800 px-3 py-2">
              <p className="text-xs font-semibold text-gray-200">Work item comments</p>
              <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                {effectiveCommentsWorkItemId
                  ? 'Threaded notes on the linked job / task. They are not stored per markup layer.'
                  : 'Comments need a work item. Link this drawing to a job or open the register from a work item.'}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-3">
              {effectiveCommentsWorkItemId ? (
                <CommentsSection
                  workItemId={effectiveCommentsWorkItemId}
                  isActive={viewMode === 'comment'}
                  showInternalToggle
                  defaultInternal
                  composerPresentation="inline"
                />
              ) : null}
            </div>
          </div>
        )}

        {/* Info sidebar */}
        {showInfo && (
          <div className="w-72 bg-gray-900 p-4 overflow-y-auto border-l border-gray-800">
            <h3 className="text-white font-semibold mb-4">Drawing Details</h3>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-gray-500 block">Name</label>
                <span className="text-white">{drawing.drawing_name}</span>
              </div>
              {drawing.drawing_number && (
                <div>
                  <label className="text-gray-500 block">Number</label>
                  <span className="text-white">{drawing.drawing_number}</span>
                </div>
              )}
              <div>
                <label className="text-gray-500 block">Revision</label>
                <span className="text-white">{drawing.revision} (v{drawing.version})</span>
              </div>
              {drawing.discipline && (
                <div>
                  <label className="text-gray-500 block">Discipline</label>
                  <span className="text-white capitalize">{drawing.discipline}</span>
                </div>
              )}
              {drawing.drawing_type && (
                <div>
                  <label className="text-gray-500 block">Type</label>
                  <span className="text-white capitalize">{drawing.drawing_type.replace(/_/g, ' ')}</span>
                </div>
              )}
              <div>
                <label className="text-gray-500 block">File Size</label>
                <span className="text-white">{formatFileSize(drawing.file_size)}</span>
              </div>
              <div>
                <label className="text-gray-500 block">Created</label>
                <span className="text-white">{new Date(drawing.created_at).toLocaleString()}</span>
              </div>
              {drawing.tags && drawing.tags.length > 0 && (
                <div>
                  <label className="text-gray-500 block mb-1">Tags</label>
                  <div className="flex flex-wrap gap-1">
                    {drawing.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 bg-gray-700 rounded text-xs text-white">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer controls */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 p-2 rounded-lg bg-gray-900/90 backdrop-blur">
        <span className="text-white/80 text-xs max-w-[140px] hidden sm:inline truncate" title="Scroll or pinch to zoom; drag to pan">
          Wheel / pinch · drag
        </span>
        <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.25))} className="p-2 rounded hover:bg-white/10 text-white">
          <ZoomOut className="w-5 h-5" />
        </button>
        <span className="text-white text-sm min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(z + 0.25, 5))} className="p-2 rounded hover:bg-white/10 text-white">
          <ZoomIn className="w-5 h-5" />
        </button>
        <div className="w-px h-6 bg-gray-600" />
        <button onClick={() => setRotation(r => (r + 90) % 360)} className="p-2 rounded hover:bg-white/10 text-white">
          <RotateCw className="w-5 h-5" />
        </button>
        <button onClick={toggleFullscreen} className="p-2 rounded hover:bg-white/10 text-white">
          {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
        </button>
        <div className="w-px h-6 bg-gray-600" />
        <button onClick={handleDownload} className="p-2 rounded hover:bg-white/10 text-white">
          <Download className="w-5 h-5" />
        </button>
        <button className="p-2 rounded hover:bg-white/10 text-white">
          <Printer className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
